import {
  messageBundle,
  storeNode,
  tsx,
} from "@arcgis/core/widgets/support/widget";
import {
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";
import Widget from "@arcgis/core/widgets/Widget";
import SearchBoxViewModel from "./SearchBoxViewModel";
import "./styles/SearchBox.scss";

import { VNode } from "../../../interfaces/interfaces";
import { watch, when } from "@arcgis/core/core/reactiveUtils";
import NavBarViewModel from "../../ui/NavBar/NavBarViewModel";
import { applyShadowStyle } from "../../utils/utils";
import { INPUT_SEARCH_STYLE } from "../../utils/shadowStyles";
import { logCapabilitySearch } from "../../utils/telemetryUtils";
import { assignPopoverId } from "../PopoverOnboarding/PopoverViewModel";

const CSS = {
  base: "esri-instant-gallery__search-settings",
  container: "esri-instant-gallery__search-settings-container",
  suggestedResultsList: "esri-instant-gallery__search-settings-results-list",
  hideSuggestedResultsList:
    "esri-instant-gallery__search-settings-results-list--hide",
  suggestedResultsListItem:
    "esri-instant-gallery__search-settings-results-list-item",
  resultListItemBold:
    "esri-instant-gallery__search-settings-results-list-item--bold",
  resultsItemWarning: "esri-search-settings__result-item-warning",
  noResultsListItem: "esri-instant-gallery__search-settings-no-results-item",
  highlightedResultItem:
    "esri-instant-gallery__search-settings-results-list-item--highlighted",
  headerTopContainer:
    "esri-instant-gallery__search-settings-header-top-container",
  headerContainer: "esri-instant-gallery__search-settings-header-container",
  closeButton: "esri-instant-gallery__search-settings-close-button",
};

@subclass("SearchBox")
export default class SearchBox extends Widget {
  constructor(params?: any) {
    super(params);
  }

  private _hasEventListener = false;
  private _suggestionsListNode: HTMLLIElement = null;
  private _suggestedResultIndex: number = null;
  /** For normal search box with suggestion behavior */
  @property()
  hideSuggestions = false;

  @property()
  hideSuggestions_masterOverride: boolean = false;

  @property()
  _placeholderToSet: string;

  @property()
  showCapabilitiesMatrixLink: boolean;

  @property()
  searchInput: HTMLCalciteInputElement = null;

  @property()
  isInFocus: boolean;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages;

  @property()
  viewModel: SearchBoxViewModel = new SearchBoxViewModel();

  @property()
  navbarVM: NavBarViewModel;

  postInitialize() {
    when(
      () => this?.appMessages !== undefined,
      () => {
        this.viewModel.appMessages = this.appMessages;
      },
      {
        initial: true,
        once: true,
      }
    );

    watch(
      () => this.navbarVM.suggestionsButtonSignal,
      () => {
        setTimeout(() => {
          this._hasEventListener = false;
          if (
            !this.navbarVM.suggestionsButtonSignal &&
            this.navbarVM._myAppsFilterState !== "" &&
            this.navbarVM._myAppsFilterState != null
          ) {
            this.navbarVM.appSearch("");
          }
          this.scheduleRender();
        }, 100);
      }
    );
  }

  setSearchBoxString(
    strVal: string,
    placeHolderText: string,
    showSuggestions?: boolean
  ) {
    if (showSuggestions != null) {
      this.hideSuggestions_masterOverride = !showSuggestions;
    }
    if (this.searchInput != null) {
      this.searchInput.value = strVal;
      this.searchInput.placeholder = placeHolderText;
      this.scheduleRender();
    } else {
      // We are defaulting to a different tab on app load, but the search doesn't exist yet, so save the intended placeholder text and set it later
      this._placeholderToSet = placeHolderText;
    }
  }

  render(): VNode {
    const suggestedResultsList = this._renderSuggestedResultsList();
    return (
      <div key="search-settings" class={CSS.base} id={assignPopoverId(1)}>
        <div
          class={CSS.container}
          afterCreate={this._setupHandlers.bind(this)}
          tabindex={this._isSearchBoxDisabled() ? "0" : "-1"}
          onkeyup={(e: KeyboardEvent) => {
            if (e.code === "Enter" || e.code === "Space") {
              this._handleSelectSearchBox();
            }
          }}
        >
          <calcite-input
            scale="l"
            bind={this}
            afterCreate={(input) => {
              this.searchInput = input;
              applyShadowStyle(INPUT_SEARCH_STYLE, input);
            }}
            afterUpdate={this._addCalciteInputEventListener.bind(this)}
            data-node-ref="searchInput"
            type="search"
            placeholder={
              this.navbarVM.navigationRoute === "myApps"
                ? this.appMessages.searchBox.search_apps
                : this.appMessages.searchBox.search_appTypes
            }
            icon="search"
            disabled={this._isSearchBoxDisabled()}
            autocomplete="off"
            onkeyup={this._handleInputKeyUp}
            onkeydown={this._handleResultKeyDown}
          />
          {suggestedResultsList}
        </div>
      </div>
    );
  }

  private _renderSuggestedResultsList(): VNode {
    const resultsListItems = this._renderSuggestedResultsListItems();
    const resultsLength = this.viewModel.searchResults.length;
    const hideStyles = {
      [CSS.hideSuggestedResultsList]:
        this.hideSuggestions_masterOverride || this.hideSuggestions,
    };

    return resultsLength > 0 && this.searchInput?.value?.length > 0 ? (
      <ul
        bind={this}
        afterCreate={storeNode}
        data-node-ref="_suggestionsListNode"
        class={this.classes(CSS.suggestedResultsList, hideStyles)}
        role="menu"
      >
        {resultsListItems}
      </ul>
    ) : resultsLength === 0 && this.searchInput?.value?.length > 0 ? (
      <ul
        class={this.classes(CSS.suggestedResultsList, hideStyles)}
        role="menu"
      >
        {this._renderNoResultsListItem()}
      </ul>
    ) : null;
  }

  private _renderNoResultsListItem(): VNode {
    return (
      <li key="no-results-item" class={CSS.noResultsListItem}>
        {this.appMessages.searchResults.noResults}
      </li>
    );
  }

  private _renderSuggestedResultsListItems(): VNode[] {
    const searchResultsArr = this.viewModel.searchResults.toArray();
    return searchResultsArr.map(
      ([searchT9N, searchResultTerm], searchResultIndex) => {
        const suggestedResult = this._renderSuggestedResultListItem(
          [searchT9N, searchResultTerm],
          searchResultIndex
        );
        return suggestedResult;
      }
    );
  }

  private _renderSuggestedResultListItem(
    [searchT9N, searchResultTerm]: [string, string],
    searchResultIndex: number
  ): VNode {
    const splitResultText = searchResultTerm
      ?.toLocaleLowerCase()
      ?.split(this.viewModel?.currentSearchValue?.toLocaleLowerCase());
    return (
      <li
        key={`${"results"}-${searchResultIndex}`}
        bind={this}
        class={this.classes(CSS.suggestedResultsListItem)}
        onclick={this._handleResultClick}
        onkeyup={this._handleInputKeyUp}
        onkeydown={this._handleResultKeyDown}
        role="menuitem"
        data-result-index={`${searchResultIndex}`}
        data-term-value={searchResultTerm}
        data-translate-key={searchT9N}
      >
        <div key={`${"resultsDiv"}-${searchResultIndex}`}>
          {this._renderResultTextWithBolding(splitResultText)}
        </div>
      </li>
    );
  }

  private _renderResultTextWithBolding(splitResultText: string[]): VNode[] {
    return splitResultText.map((searchWordPart: string, index: number) => {
      return splitResultText.length - 1 !== index ? (
        [
          <span key={`${"resultsSpan"}-${index}_1`}>{searchWordPart}</span>,
          <span
            key={`${"resultsSpan"}-${index}_2`}
            class={CSS.resultListItemBold}
          >
            {this?.viewModel?.currentSearchValue?.toLocaleLowerCase()}
          </span>,
        ]
      ) : (
        <span key={`${"resultsSpan"}-${index}`}>{searchWordPart}</span>
      );
    });
  }

  private _focusInSearchBox(): void {
    this.isInFocus = true;
    this.navbarVM.learnMoreSidebarSignal = false;
  }

  private _addCalciteInputEventListener(node: HTMLCalciteInputElement): void {
    if (this._hasEventListener) {
      return;
    }
    this._hasEventListener = true;
    node.addEventListener("calciteInputInput", (e: CustomEvent) => {
      if (this.navbarVM.navigationRoute === "myApps") {
        // "MY APPS" SEARCH BOX BEHAVIOR
        const value = (e?.target as HTMLCalciteInputElement)?.value?.trim();
        this.navbarVM.appSearch(value);
      } else {
        // "BROWSE ALL" SEARCH BOX BEHAVIOR
        this._focusInSearchBox();
        this._calciteInputInput(e);
      }
    });
    node.addEventListener("focus", (e: CustomEvent) => {
      if (this.navbarVM.navigationRoute !== "myApps") {
        this._calciteInputFocus();
      }
    });

    node.addEventListener("click", (e: CustomEvent) => {
      if (this.navbarVM.navigationRoute !== "myApps") {
        this._focusInSearchBox();
      }
    });
  }

  private _calciteInputInput(e: CustomEvent): void {
    this._suggestedResultIndex = null;
    this.viewModel.handleSearch(e);
    this.scheduleRender();
  }

  private _calciteInputFocus(): void {
    this.hideSuggestions = false;
    this._showCapabilitiesMatrixLink();
    this.scheduleRender();
    this._focusInSearchBox();
  }

  private _searchBoxFocusOut(): void {
    this.hideSuggestions = true;
    this._hideCapabilitiesMatrixLink();
    this.scheduleRender();
  }

  private _showCapabilitiesMatrixLink(): void {
    if (!this.navbarVM.portal.isPortal) {
      this.showCapabilitiesMatrixLink = true;
    }
    this.scheduleRender();
  }

  private _hideCapabilitiesMatrixLink(): void {
    this.showCapabilitiesMatrixLink = false;
    this.scheduleRender();
  }

  private _previousFocusedListItem: HTMLLIElement;
  private _removePreviousHighlight() {
    if (this._previousFocusedListItem != null) {
      this._previousFocusedListItem.classList.remove(CSS.highlightedResultItem);
    }
  }

  private _handleInputKeyUp(e: KeyboardEvent): void {
    if (e.code === "Escape") {
      this.searchInput.setFocus();
    }
    if (!this._suggestionsListNode) {
      return;
    }

    if (e.code === "ArrowDown" || e.code === "ArrowUp") {
      const listItemNodes =
        this._suggestionsListNode.getElementsByTagName("li");
      const firstListItemNode = listItemNodes[0];
      const lastItemIndex = listItemNodes.length - 1;
      const lastListItemNode = listItemNodes[lastItemIndex];
      if (listItemNodes.length > 0) {
        if (this._suggestedResultIndex === null) {
          let currentListItem: HTMLLIElement;
          if (e.code === "ArrowDown") {
            this._suggestedResultIndex = 0;
            currentListItem = firstListItemNode;
          } else if (e.code === "ArrowUp") {
            this._suggestedResultIndex = lastItemIndex;
            currentListItem = lastListItemNode;
          }
          currentListItem.classList.add(CSS.highlightedResultItem);
          this._removePreviousHighlight();
          this._previousFocusedListItem = currentListItem;
        } else if (
          (e.code === "ArrowDown" &&
            this._suggestedResultIndex === listItemNodes.length - 1) ||
          (e.code === "ArrowUp" && this._suggestedResultIndex === 0)
        ) {
          this._suggestedResultIndex = null;
          this.searchInput.setFocus();
          this._removePreviousHighlight();
          this._previousFocusedListItem = null;
        } else {
          if (e.code === "ArrowDown") {
            this._suggestedResultIndex += 1;
          } else if (e.code === "ArrowUp") {
            this._suggestedResultIndex -= 1;
          }
          const listItemNode = listItemNodes[this._suggestedResultIndex];
          listItemNode.classList.add(CSS.highlightedResultItem);
          this._removePreviousHighlight();
          this._previousFocusedListItem = listItemNode;
        }
      }
    }
  }

  private _handleResultClick(e: Event): void {
    const node = e.currentTarget as HTMLLIElement;
    const selectionValue = node.getAttribute("data-term-value");
    const translationKey = node.getAttribute("data-translate-key");
    this.handleResultSelectionNode(translationKey, selectionValue);
  }

  private _handleResultKeyDown(e: KeyboardEvent): void {
    if (e.code === "Tab") {
      return;
    }
    if (this.hideSuggestions) {
      this.hideSuggestions = false;
    }
    if (e.type === "keydown" && e.code !== "Space" && e.code !== "Enter") {
      return;
    }
    const selectedNode = this._suggestionsListNode?.querySelector(
      `[data-result-index='${this._suggestedResultIndex}']`
    ) as HTMLLIElement;
    if (!selectedNode) {
      return;
    }
    const selectionValue = selectedNode.getAttribute("data-term-value");
    const translationKey = selectedNode.getAttribute("data-translate-key");
    this.handleResultSelectionNode(translationKey, selectionValue);
  }

  handleResultSelectionNode(
    translationKey: string,
    selectionValue: string
  ): void {
    this._suggestedResultIndex = null;
    this.hideSuggestions = true;
    this.searchInput.value = null;

    this.navbarVM.addCapabililty([translationKey, selectionValue]);

    logCapabilitySearch(
      this.navbarVM.telemetry,
      selectionValue,
      this.navbarVM.navigationRoute,
      this.navbarVM.filterChips.chips.length
    );

    this._hideCapabilitiesMatrixLink();
  }

  private _setupHandlers(encompassingDiv: HTMLDivElement) {
    encompassingDiv.addEventListener(
      "click",
      (e: KeyboardEvent) => {
        this._handleSelectSearchBox();
      },
      { capture: true }
    );
    encompassingDiv.addEventListener("focusout", (e: FocusEvent) => {
      const elemFocusWentTo: HTMLElement = e.relatedTarget as HTMLElement;
      let focusedElemIsDescendant = false; // is elemFocusWentTo a descendant of encompassingDiv?
      let descendantChainMember = elemFocusWentTo;

      // loop up the parentElement until DOM Document is reached (document.parentElement === null)
      while (descendantChainMember != null) {
        if (descendantChainMember === encompassingDiv) {
          focusedElemIsDescendant = true;
        }
        descendantChainMember = descendantChainMember.parentElement;
      }
      if (!focusedElemIsDescendant) {
        const isElemFocusWentToNull = elemFocusWentTo === null; // implies a click happened to cause focusout
        setTimeout(
          () => {
            this._searchBoxFocusOut();
          },
          isElemFocusWentToNull ? 500 : 0
        );
      }
    });
  }

  private _isSearchBoxDisabled(): boolean {
    return false;
  }

  private _handleSelectSearchBox(): void {
    if (
      this.navbarVM.suggestionsButtonSignal &&
      this.navbarVM.navigationRoute === "browseAll"
    ) {
      setTimeout(() => {
        this.searchInput.setFocus();
      }, 100);
    }
  }
}
